import type { ReactNode } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type SnackbarKey, useSnackbar } from 'notistack';
import { nativePageAvailable } from '~/nativeLenses/nativePages';

import { unresolvableViewColumn } from '~/pages/LeLens/cardDatasetBinding';
import { bindFilterToCards } from '~/pages/LeLens/filterBindings';
import { lensOperationsBetween } from '~/pages/LeLens/lensOperations';

import { REPORTING_LE_LENSES_PATH, reportingLeLensCardsPath, reportingLeLensPath } from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type ApiError from '~/types/ApiError';
import type { Lens, LensResponse } from '~/types/leLens';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { encodedJsonBody } from './encodedJsonBody';
import { lensUndoAction } from './lensUndoToast';
import { type LensAppliedChange, lensUndoEntryKey, postLensChange, rememberOwnLensChange } from './useLensChange';
import { lensWriteFailureMessage } from './useUpdateReportingLeLensErrors';

export const reportingLeLensQueryKeys = {
  all: ['reporting', 'le-lens'] as const,
  lists: () => [...reportingLeLensQueryKeys.all, 'list'] as const,
  details: () => [...reportingLeLensQueryKeys.all, 'detail'] as const,
  detail: (lensId: string) => [...reportingLeLensQueryKeys.details(), lensId] as const,
};

/** Every Lens the caller owns, plus anything published. */
export function useGetReportingLeLenses() {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: reportingLeLensQueryKeys.lists(),
    select: (lenses: Array<Lens>) => lenses.filter((lens) => nativePageAvailable(lens, user)),
    queryFn: async ({ signal }) => {
      const response = await requestWithTimeout<{ lenses?: Lens[] }>(
        resolveReportingLeLensUrl(REPORTING_LE_LENSES_PATH),
        { signal },
        'Lenses are taking too long to load. Please retry.',
      );
      return response.lenses ?? [];
    },
    retry: false,
  });
}

/** Loads an LE Lens definition from svc-reporting. */
export function useGetReportingLeLens(lensId: string | undefined, _openId?: string) {
  return useQuery({
    queryKey: reportingLeLensQueryKeys.detail(lensId ?? ''),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(reportingLeLensPath(lensId!)), {
        signal,
      })) as LensResponse;
      return response;
    },
    enabled: Boolean(lensId),
    retry: false,
    refetchInterval: (query) => (query.state.data?.drafting ? 2000 : false),
  });
}

type UpdateLensInput = Pick<Lens, 'id' | 'title' | 'description' | 'spec' | 'version'> & {
  /** Shown on success; callers describe what they changed. */
  successMessage?: string;
  /** Optional action shown alongside the success toast. */
  successAction?: (lens: Lens) => (key: SnackbarKey) => ReactNode;
  /** A background save that already updates its own visible receipt should not add a toast. */
  quietSuccess?: boolean;
  /**
   * Let the caller state the failure itself. A dialog that stays open for the retry
   * should say what happened inside itself: a toast can cover its own primary action,
   * so the reader is told to try again by the thing standing on the button.
   */
  quietFailure?: boolean;
  /**
   * Re-run the card queries. Only needed when the SQL changed: formatting and
   * sorting are applied to rows already in hand, so refetching there would
   * bill BigQuery and blank the cards for a presentation-only edit.
   */
  refetchData?: boolean;
  /** The scope confirmation gate's own guard: reject this save if `spec.scope` is absent. Sent
   *  only from the plan gate's append save, once its clarifications are resolved; every other
   *  caller of this hook omits it and is unaffected. */
  requireScope?: boolean;
  operationsBase?: Lens;
};

export function useUpdateReportingLeLens() {
  const queryClient = useQueryClient();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      id,
      successMessage: _successMessage,
      successAction: _successAction,
      quietSuccess: _quietSuccess,
      refetchData: _refetchData,
      quietFailure: _quietFailure,
      requireScope,
      operationsBase,
      ...lens
    }: UpdateLensInput): Promise<{ lens: Lens; receipt?: LensAppliedChange }> => {
      const spec = {
        ...lens.spec,
        cards: (lens.spec.filters ?? []).reduce(
          (cards, filter) => bindFilterToCards(cards, filter, lens.spec.datasets),
          lens.spec.cards,
        ),
      };
      const cached = queryClient.getQueryData<LensResponse>(reportingLeLensQueryKeys.detail(id))?.lens;
      const base = operationsBase?.version === lens.version ? operationsBase : (cached ?? operationsBase);
      const operations =
        base && base.version === lens.version && !requireScope
          ? lensOperationsBetween(base, { title: lens.title, description: lens.description, spec })
          : null;
      if (base && operations && !operations.length) return { lens: base };
      if (base && operations) {
        const receipt = await postLensChange(id, { baseVersion: lens.version, operations });
        return { lens: receipt.lens ?? base, receipt };
      }
      return (await request(resolveReportingLeLensUrl(reportingLeLensPath(id)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: encodedJsonBody({ ...lens, ...(requireScope ? { requireScope } : {}), spec }),
      })) as { lens: Lens };
    },
    onSuccess: ({ lens, receipt }, variables) => {
      queryClient.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(lens.id), (current) => ({
        lens,
        access: current?.access ?? { canEdit: true },
      }));
      // refetch rather than invalidate: the client holds a 60s staleTime, and
      // invalidate only reaches queries it considers active, so a card could
      // keep rendering the pre-save spec until the page was reloaded.
      void queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(lens.id) });
      if (variables.refetchData !== false) {
        void queryClient.refetchQueries({ queryKey: [...reportingLeLensQueryKeys.detail(lens.id), 'data'] });
      }
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      if (receipt) rememberOwnLensChange(queryClient, receipt);
      for (const problem of receipt?.problems.filter((entry) => entry.kind === 'refused') ?? []) {
        enqueueSnackbar(`Not changed: ${problem.message}`, { variant: 'warning' });
      }
      if (!variables.quietSuccess) {
        enqueueSnackbar(variables.successMessage ?? 'Lens updated', {
          variant: 'success',
          action: receipt?.applied
            ? lensUndoAction(queryClient, lensUndoEntryKey(lens.id, [receipt.toVersion]), {
                enqueueSnackbar,
                closeSnackbar,
              })
            : variables.successAction?.(lens),
        });
      }
    },
    onError: async (error: Error, variables) => {
      // The server's WHERE clause matches on version as well as id and owner, so a save built
      // from a stale spec is rejected rather than applied and nothing of anyone else's was
      // overwritten. What the reader loses is their own edit, and the generic failure left them
      // looking at the spec they had just edited with no way to tell it had not been saved. So
      // this names what happened and puts the current dashboard back on screen.
      //
      // The message stays vague about the cause on purpose: the endpoint reports "changed by
      // someone else" and "you no longer have access" as the same 409 and cannot tell them apart.
      if ((error as { status?: number }).status === 409) {
        // Both the detail and the list query hold a version. The save dialog reads its
        // destination out of the list, so refetching only the detail left the retry
        // sending the same stale version and failing again: a loop with no way out.
        await Promise.all([
          queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(variables.id) }),
          queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.lists() }),
        ]);
        // A conflict is still a failure the caller may be stating itself. The snackbar anchors
        // over a dialog's primary action, so a toast here sat on the very button the dialog was
        // telling the reader to press again and intercepted the click.
        if (!variables.quietFailure) {
          enqueueSnackbar(
            ((error as ApiError).responseBody as { message?: string } | undefined)?.message ??
              'Your change was not saved: this Lens has changed since you opened it, or you no longer have edit access. It has been reloaded, so make the change again.',
            { variant: 'warning', key: `lens-save-${variables.id}`, preventDuplicate: true },
          );
        }
        return;
      }
      if (!variables.quietFailure) {
        enqueueSnackbar(lensWriteFailureMessage(error, 'save'), {
          variant: 'error',
          key: `lens-save-${variables.id}`,
          preventDuplicate: true,
        });
      }
    },
    retry: false,
  });
}

type AddLensCardInput = {
  id: string;
  version: number;
  card: Lens['spec']['cards'][number];
  successMessage?: string;
  successAction?: (lens: Lens) => (key: SnackbarKey) => ReactNode;
  /** See UpdateLensInput.quietFailure. */
  quietFailure?: boolean;
};

/** Adds one answer-derived card without replacing unrelated dashboard edits. */
export function useAddReportingLeLensCard() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, version, card }: AddLensCardInput) => {
      // An answer-derived card can name a measure the question implied rather
      // than one the dataset returns. Saved, it is a dead tile on every load.
      // The service refuses the same card; refusing it here names the column
      // and the dataset instead of returning a contract message.
      const target = queryClient.getQueryData<LensResponse>(reportingLeLensQueryKeys.detail(id))?.lens;
      const dataset = card.datasetId ? target?.spec?.datasets?.find((entry) => entry.id === card.datasetId) : undefined;
      if (card.view && dataset) {
        const unresolvable = unresolvableViewColumn(card.view, dataset);
        if (unresolvable) throw new Error(unresolvable);
      }
      return (await request(resolveReportingLeLensUrl(reportingLeLensCardsPath(id)), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encodedJsonBody({
          card: (target?.spec.filters ?? []).reduce(
            (cards, filter) => bindFilterToCards(cards, filter, target?.spec.datasets),
            [card],
          )[0],
          version,
        }),
      })) as { lens: Lens };
    },
    onSuccess: ({ lens }, variables) => {
      queryClient.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(lens.id), (current) => ({
        lens,
        access: current?.access ?? { canEdit: true },
      }));
      void queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(lens.id) });
      void queryClient.refetchQueries({ queryKey: [...reportingLeLensQueryKeys.detail(lens.id), 'data'] });
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      enqueueSnackbar(variables.successMessage ?? 'Added to Lens', {
        variant: 'success',
        action: variables.successAction?.(lens),
      });
    },
    onError: async (error: Error, variables) => {
      if ((error as { status?: number }).status === 409) {
        // Same reason as the update hook: the dialog's destination version comes
        // from the list, so a detail-only refetch cannot make the retry succeed.
        await Promise.all([
          queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(variables.id) }),
          queryClient.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.lists() }),
        ]);
        if (!variables.quietFailure) {
          enqueueSnackbar('This Lens changed before the card was added. It has been reloaded, so try again.', {
            variant: 'warning',
          });
        }
        return;
      }
      if (!variables.quietFailure) {
        enqueueSnackbar(lensWriteFailureMessage(error, 'add'), { variant: 'error' });
      }
    },
    retry: false,
  });
}

type CreateLensInput = Pick<Lens, 'title'> & {
  description?: string | null;
  spec?: Lens['spec'];
  /** The plan this dashboard was built from, so Reporting can join brief to Lens. */
  planId?: string;
  /** Shown on success; callers describe what they created. */
  successMessage?: string;
  /** Optional action shown alongside the success toast. */
  successAction?: (lens: Lens) => (key: SnackbarKey) => ReactNode;
  /** See UpdateLensInput.quietFailure. */
  quietFailure?: boolean;
  /** See UpdateLensInput.requireScope. Sent only from the plan gate's create save. */
  requireScope?: boolean;
};

export function useCreateReportingLeLens() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      successAction: _successAction,
      successMessage: _successMessage,
      quietFailure: _quietFailure,
      requireScope,
      ...input
    }: CreateLensInput) => {
      return (await request(resolveReportingLeLensUrl(REPORTING_LE_LENSES_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encodedJsonBody({
          title: input.title,
          description: input.description ?? null,
          spec: input.spec ?? { version: 1, cards: [] },
          ...(input.planId ? { planId: input.planId } : {}),
          ...(requireScope ? { requireScope } : {}),
        }),
      })) as { lens: Lens };
    },
    onSuccess: ({ lens }, variables) => {
      queryClient.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(lens.id), {
        lens,
        access: { canEdit: true },
      });
      void queryClient.invalidateQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(lens.id) });
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      enqueueSnackbar(variables.successMessage ?? 'Lens created', {
        variant: 'success',
        action: variables.successAction?.(lens),
      });
    },
    onError: (error: Error, variables) => {
      if (!variables.quietFailure) {
        enqueueSnackbar(lensWriteFailureMessage(error, 'create'), { variant: 'error' });
      }
    },
    retry: false,
  });
}

export function useDeleteReportingLeLens() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (lensId: string) => {
      await request(resolveReportingLeLensUrl(reportingLeLensPath(lensId)), { method: 'DELETE' });
      return lensId;
    },
    onSuccess: (lensId) => {
      queryClient.removeQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId) });
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      enqueueSnackbar('Lens deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Couldn\u2019t delete this Lens: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
    retry: false,
  });
}

/** Deletes several owned Lenses as one list action and refreshes the library once. */
export function useDeleteReportingLeLenses() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (lensIds: Array<string>) => {
      await Promise.all(
        lensIds.map((lensId) => request(resolveReportingLeLensUrl(reportingLeLensPath(lensId)), { method: 'DELETE' })),
      );
      return lensIds;
    },
    onSuccess: (lensIds) => {
      lensIds.forEach((lensId) => queryClient.removeQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId) }));
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      enqueueSnackbar(`${lensIds.length} ${lensIds.length === 1 ? 'Lens' : 'Lenses'} deleted`, { variant: 'success' });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      enqueueSnackbar(`Couldn’t delete the selected Lenses: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
    retry: false,
  });
}
